import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";
import { CircularProgress } from "@mui/material";
import { MAX_SIZE_MB } from "../../helper";
import FacilityGalleryModal from "./FacilityGalleryModal";
import CollectionsIcon from "@mui/icons-material/Collections";

const customModalStyles = {
  content: {
    padding: "24px",
    width: "90%",
    maxWidth: "680px",
    margin: "auto",
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
    maxHeight: "90vh",
    overflowY: "auto",
  },
};

const fieldStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: "14px",
  borderRadius: "6px",
  border: "1px solid #cbd5e1",
  boxSizing: "border-box",
  outline: "none",
};

const labelStyle = {
  display: "block",
  fontWeight: "600",
  fontSize: "13px",
  color: "#334155",
  marginBottom: "6px",
};

const emptyForm = {
  title: "",
  slug: "",
  subtitle: "",
  badge: "",
  description: "",
  features: "",
  order: 0,
  isActive: true,
  image: "",
};

export default function CampusFacilities() {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [galleryModalFacility, setGalleryModalFacility] = useState(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);

  const openGalleryModal = (facility) => {
    setGalleryModalFacility(facility);
    setShowGalleryModal(true);
  };

  const closeGalleryModal = () => {
    setShowGalleryModal(false);
    setGalleryModalFacility(null);
  };

  const handleGalleryUpdated = (updatedGallery) => {
    if (!galleryModalFacility) return;
    const targetKey = galleryModalFacility.slug || galleryModalFacility.id || galleryModalFacility._id;
    setFacilities((prev) =>
      prev.map((f) => {
        const key = f.slug || f.id || f._id;
        if (key === targetKey) {
          return { ...f, gallery: updatedGallery };
        }
        return f;
      })
    );
  };

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("campus-facilities/all");
      const list = response?.data?.data || [];
      // Sort by order ascending
      list.sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));
      setFacilities(list);
    } catch (error) {
      console.error("Error fetching campus facilities:", error);
      Swal.fire("Error", "Failed to load campus facilities", "error");
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > MAX_SIZE_MB) {
      alert(`File size exceeds ${MAX_SIZE_MB} MB. Please upload a smaller file.`);
      e.target.value = "";
      return;
    }
    setImgFile(file);
    setImgPreview(URL.createObjectURL(file));
  };

  const buildPayload = () => {
    const data = new FormData();
    data.append("title", formData.title);
    data.append("slug", formData.slug || "");
    data.append("subtitle", formData.subtitle || "");
    data.append("badge", formData.badge || "");
    data.append("description", formData.description || "");
    data.append("features", formData.features || "");
    data.append("order", formData.order || 0);
    data.append("isActive", formData.isActive ? "1" : "0");
    if (imgFile) {
      data.append("image", imgFile);
    }
    return data;
  };

  const closeCreate = () => {
    setShowCreateModal(false);
    setFormData(emptyForm);
    setImgFile(null);
    setImgPreview(null);
  };

  const closeEdit = () => {
    setShowEditModal(false);
    setEditId(null);
    setFormData(emptyForm);
    setImgFile(null);
    setImgPreview(null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert("Facility Title is required.");
      return;
    }
    setButtonLoading(true);
    try {
      await axiosInstance.post("campus-facilities/create", buildPayload());
      await fetchFacilities();
      closeCreate();
      Swal.fire("Success!", "Campus facility created successfully!", "success");
    } catch (error) {
      console.error("Error creating campus facility:", error);
      const msg = error.response?.data?.message || "Failed to create campus facility";
      Swal.fire("Error", msg, "error");
    }
    setButtonLoading(false);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert("Facility Title is required.");
      return;
    }
    setButtonLoading(true);
    try {
      const payload = buildPayload();
      payload.append("_method", "PUT");
      await axiosInstance.post(`campus-facilities/change/${editId}`, payload);
      await fetchFacilities();
      closeEdit();
      Swal.fire("Success!", "Campus facility updated successfully!", "success");
    } catch (error) {
      console.error("Error updating campus facility:", error);
      const msg = error.response?.data?.message || "Failed to update campus facility";
      Swal.fire("Error", msg, "error");
    }
    setButtonLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Delete Facility?",
        text: "Are you sure you want to delete this campus facility?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#64748b",
        confirmButtonText: "Yes, delete it!",
      });
      if (!result.isConfirmed) return;

      await axiosInstance.delete(`campus-facilities/delete/${id}`);
      await fetchFacilities();
      Swal.fire("Deleted!", "Campus facility deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting campus facility:", error);
      Swal.fire("Error", "Failed to delete campus facility", "error");
    }
  };

  const handleView = async (id) => {
    try {
      setShowEditModal(true);
      setEditId(id);
      setImgFile(null);
      const res = await axiosInstance.get(`campus-facilities/single/${id}`);
      const data = res?.data?.data;
      if (!data) return;

      const featuresText = Array.isArray(data.features)
        ? data.features.join("\n")
        : typeof data.features === "string"
        ? data.features
        : "";

      setFormData({
        title: data.title || "",
        slug: data.slug || "",
        subtitle: data.subtitle || "",
        badge: data.badge || "",
        description: data.description || "",
        features: featuresText,
        order: data.order ?? 0,
        isActive: data.isActive !== false,
        image: data.image || "",
      });
      setImgPreview(data.image || null);
    } catch (error) {
      console.error("Error fetching facility details:", error);
      Swal.fire("Error", "Could not load facility details", "error");
    }
  };

  const renderFormFields = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={labelStyle}>Facility Title *</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="e.g. Swimming Pool & Sports Complex"
            required
            style={fieldStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>URL Slug (optional)</label>
          <input
            type="text"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            placeholder="e.g. swimming-pool"
            style={fieldStyle}
          />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={labelStyle}>Subtitle / Headline</label>
          <input
            type="text"
            name="subtitle"
            value={formData.subtitle}
            onChange={handleChange}
            placeholder="e.g. State-of-the-Art Athletic Complex"
            style={fieldStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Badge Tag</label>
          <input
            type="text"
            name="badge"
            value={formData.badge}
            onChange={handleChange}
            placeholder="e.g. Sports & Fitness"
            style={fieldStyle}
          />
        </div>
      </div>

      <div>
        <label style={labelStyle}>Description / Overview</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={4}
          placeholder="Comprehensive details and overview of this campus facility..."
          style={{ ...fieldStyle, resize: "vertical" }}
        />
      </div>

      <div>
        <label style={labelStyle}>Key Features / Highlights (one per line)</label>
        <textarea
          name="features"
          value={formData.features}
          onChange={handleChange}
          rows={4}
          placeholder="Olympic-standard 50m pool&#10;Certified coaching staff&#10;Dedicated ladies & gents timings&#10;Lockers & shower facilities"
          style={{ ...fieldStyle, resize: "vertical" }}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "center" }}>
        <div>
          <label style={labelStyle}>Sort Order</label>
          <input
            type="number"
            name="order"
            value={formData.order}
            onChange={handleChange}
            style={fieldStyle}
          />
        </div>
        <div style={{ paddingTop: 20 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 600 }}>
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              style={{ width: 18, height: 18 }}
            />
            <span>Active & Visible on Website</span>
          </label>
        </div>
      </div>

      <div>
        <label style={labelStyle}>Facility Photo / Image</label>
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={fieldStyle}
        />
        {imgPreview && (
          <div style={{ marginTop: 10 }}>
            <img
              src={imgPreview}
              alt="Facility Preview"
              style={{
                width: "100%",
                maxHeight: 220,
                objectFit: "cover",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ padding: "10px 0" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 24, color: "#1e293b", fontWeight: 700 }}>
            Campus Facilities Manager
          </h2>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 14 }}>
            Create, update content & photos, and organize all university facilities displayed on /facilities.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setFormData({ ...emptyForm, order: facilities.length + 1 });
            setImgFile(null);
            setImgPreview(null);
            setShowCreateModal(true);
          }}
          style={{
            padding: "10px 20px",
            backgroundColor: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 14,
            boxShadow: "0 4px 10px rgba(37, 99, 235, 0.2)",
          }}
        >
          + Add New Facility
        </button>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <CircularProgress />
        </div>
      ) : facilities.length === 0 ? (
        <div
          style={{
            background: "#fff",
            padding: 40,
            borderRadius: 12,
            textAlign: "center",
            border: "1px dashed #cbd5e1",
          }}
        >
          <p style={{ color: "#64748b", fontSize: 16, margin: 0 }}>
            No campus facilities found. Click "+ Add New Facility" to add one.
          </p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 20,
          }}
        >
          {facilities.map((item) => (
            <div
              key={item._id || item.id}
              style={{
                background: "#fff",
                borderRadius: 12,
                overflow: "hidden",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
            >
              <div style={{ position: "relative", height: 180, background: "#f1f5f9" }}>
                <img
                  src={item.image || "/images/okara-university-campus.webp"}
                  alt={item.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
                {item.badge && (
                  <span
                    style={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      background: "rgba(15, 23, 42, 0.8)",
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "4px 10px",
                      borderRadius: 20,
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    {item.badge}
                  </span>
                )}
                <span
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    background: item.isActive !== false ? "#10b981" : "#ef4444",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "4px 8px",
                    borderRadius: 6,
                  }}
                >
                  {item.isActive !== false ? "Active" : "Hidden"}
                </span>
              </div>

              <div style={{ padding: 18, flex: 1, display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                  <h3 style={{ margin: 0, fontSize: 17, color: "#1e293b", fontWeight: 700 }}>
                    {item.title}
                  </h3>
                  <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>
                    #{item.order ?? 0}
                  </span>
                </div>

                {item.subtitle && (
                  <p style={{ margin: "4px 0 0", color: "#2563eb", fontSize: 13, fontWeight: 600 }}>
                    {item.subtitle}
                  </p>
                )}

                <p
                  style={{
                    margin: "10px 0 0",
                    color: "#64748b",
                    fontSize: 13,
                    lineHeight: 1.5,
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    flex: 1,
                  }}
                >
                  {item.description || "No description provided."}
                </p>

                {item.features && item.features.length > 0 && (
                  <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {(Array.isArray(item.features) ? item.features : []).slice(0, 3).map((f, idx) => (
                      <span
                        key={idx}
                        style={{
                          background: "#f1f5f9",
                          color: "#475569",
                          fontSize: 11,
                          padding: "2px 8px",
                          borderRadius: 4,
                          fontWeight: 500,
                        }}
                      >
                        ✓ {f}
                      </span>
                    ))}
                    {(item.features || []).length > 3 && (
                      <span style={{ fontSize: 11, color: "#94a3b8", alignSelf: "center" }}>
                        +{item.features.length - 3} more
                      </span>
                    )}
                  </div>
                )}

                <div
                  style={{
                    marginTop: 16,
                    paddingTop: 12,
                    borderTop: "1px solid #f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => openGalleryModal(item)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 6,
                      border: "1px solid #bfdbfe",
                      backgroundColor: "#eff6ff",
                      color: "#1d4ed8",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                    title="Upload or manage photos for this facility"
                  >
                    <CollectionsIcon style={{ fontSize: 16 }} />
                    <span>Photo Gallery ({Array.isArray(item.gallery) ? item.gallery.length : 0})</span>
                  </button>

                  <Actions
                    courseId={item._id || item.id}
                    onEdit={handleView}
                    onDelete={handleDelete}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onRequestClose={closeCreate}
        style={customModalStyles}
        contentLabel="Add Campus Facility"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: "#1e293b" }}>Add New Campus Facility</h2>
          <button
            type="button"
            onClick={closeCreate}
            style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleCreate}>
          {renderFormFields()}
          <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              onClick={closeCreate}
              style={{
                padding: "10px 18px",
                background: "#f1f5f9",
                border: "1px solid #cbd5e1",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={buttonLoading}
              style={{
                padding: "10px 24px",
                backgroundColor: "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {buttonLoading ? <CircularProgress size={20} color="inherit" /> : "Save Facility"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onRequestClose={closeEdit}
        style={customModalStyles}
        contentLabel="Edit Campus Facility"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: "#1e293b" }}>Edit Campus Facility</h2>
          <button
            type="button"
            onClick={closeEdit}
            style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleEdit}>
          {renderFormFields()}
          <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              onClick={closeEdit}
              style={{
                padding: "10px 18px",
                background: "#f1f5f9",
                border: "1px solid #cbd5e1",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={buttonLoading}
              style={{
                padding: "10px 24px",
                backgroundColor: "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {buttonLoading ? <CircularProgress size={20} color="inherit" /> : "Update Facility"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Facility Photo Gallery Modal (Admin only - uploads up to 50 photos) */}
      <FacilityGalleryModal
        facility={galleryModalFacility}
        isOpen={showGalleryModal}
        onClose={closeGalleryModal}
        onGalleryUpdated={handleGalleryUpdated}
      />
    </div>
  );
}
